import { ExpandIcon, ShrinkIcon } from "@100mslive/react-icons";
import React, { useCallback, useContext, useRef, useState } from "react";
import { StyledVideoTile, Video, VideoTileStats } from "@100mslive/react-ui";
import {
  selectLocalPeerID,
  selectPeerByID,
  selectScreenShareAudioByPeerID,
  selectScreenShareByPeerID,
  useHMSStore,
} from "@100mslive/react-sdk";
import { useIsHeadless, useUISettings } from "./AppData/useUISettings";

import { LiveHodoContext } from "../contexts/LiveHodoContext";
import TileMenu from "./TileMenu";
import { UI_SETTINGS } from "../common/constants";
import { getVideoTileLabel } from "./peerTileUtils";
import screenfull from "screenfull";
import { useFullscreen } from "react-use";

const labelStyles = {
  position: "unset",
  width: "100%",
  textAlign: "center",
  transform: "none",
  mt: "$2",
  flexShrink: 0,
};

const Tile = ({ peerId, width = "100%", height = "100%", isSmallTile }) => {
  const { setSelectedScreenShare } = useContext(LiveHodoContext);
  const isLocal = useHMSStore(selectLocalPeerID) === peerId;
  const track = useHMSStore(selectScreenShareByPeerID(peerId));
  const peer = useHMSStore(selectPeerByID(peerId));
  const isAudioOnly = useUISettings(UI_SETTINGS.isAudioOnly);
  const isHeadless = useIsHeadless();
  const [isMouseHovered, setIsMouseHovered] = useState(false);
  const showStatsOnTiles = useUISettings(UI_SETTINGS.showStatsOnTiles);
  const label = getVideoTileLabel({
    peerName: peer?.name || "",
    peerRole: peer?.roleName,
    isLocal: false,
    track,
  });
  const fullscreenRef = useRef(null);
  // fullscreen is for desired state
  const [fullscreen, setFullscreen] = useState(false);
  // isFullscreen is for true state
  const isFullscreen = useFullscreen(fullscreenRef, fullscreen, {
    onClose: () => setFullscreen(false),
  });
  const isFullScreenSupported = screenfull.isEnabled;
  const audioTrack = useHMSStore(selectScreenShareAudioByPeerID(peer?.id));

  const handleSelectedScreenShare = useCallback(peer => {
    if (!peer) return;
    // console.log(peer);
    setSelectedScreenShare(peer);
  }, []);

  return (
    <StyledVideoTile.Root
      css={{
        width,
        height,
        "@lg": { width: "100%" },
      }}
      data-testid="screenshare_tile"
    >
      {peer ? (
        <StyledVideoTile.Container
          transparentBg
          ref={fullscreenRef}
          css={{
            flexDirection: "column",
            cursor: isSmallTile ? "pointer" : "default",
          }}
          onMouseEnter={() => setIsMouseHovered(true)}
          onMouseLeave={() => {
            setIsMouseHovered(false);
          }}
        >
          {showStatsOnTiles && !isSmallTile ? (
            <VideoTileStats
              audioTrackID={audioTrack?.id}
              videoTrackID={track?.id}
              peerID={peerId}
              isLocal={isLocal}
            />
          ) : null}
          {isFullScreenSupported && !isHeadless ? (
            <StyledVideoTile.FullScreenButton
              css={{
                display: isSmallTile ? "none" : "flex",
                "@lg": {
                  display: "flex",
                },
              }}
              onClick={() => setFullscreen(!fullscreen)}
            >
              {isFullscreen ? <ShrinkIcon /> : <ExpandIcon />}
            </StyledVideoTile.FullScreenButton>
          ) : null}
          {track ? (
            <Video
              screenShare={true}
              mirror={peer.isLocal && track?.source === "regular"}
              attach={!isAudioOnly}
              trackId={track.id}
              onClick={() => handleSelectedScreenShare(peer)}
            />
          ) : null}
          <StyledVideoTile.Info css={labelStyles}>{label}</StyledVideoTile.Info>
          {isMouseHovered && !isHeadless && !isSmallTile && !peer?.isLocal ? (
            <TileMenu
              isScreenshare
              peerID={peer?.id}
              audioTrackID={audioTrack?.id}
              videoTrackID={track?.id}
            />
          ) : null}
        </StyledVideoTile.Container>
      ) : null}
    </StyledVideoTile.Root>
  );
};

const ScreenshareTile = React.memo(Tile);

export default ScreenshareTile;
